import { useEffect, useState } from "react";

// The procurement "cart" (2026-10-30) — medicines picked across pages, tabs and filters, then
// raised as ONE purchase order. Client-side only: there is no cart on the API, the order is a
// single POST /inventory/orders at the end (see ProcurementCart.jsx).
//
// A module-level store rather than component state, because each tab (Overall/New/Reorder) is a
// separate route and remounts — state kept in a view would be lost on every tab or page change.
// Mirrored to localStorage so a refresh doesn't empty it.
//
// A line is { itemId, itemInventoryId, medicineName, stripSize, quantity, view, lapsingEnabled, lookaheadDays } —
// `quantity` is TABLETS, kept as a string while it's being edited. It starts as the listing's
// suggestion at the moment of adding (blank when there is none) and is edited in the cart.

const STORAGE_KEY = "pillup-procurement-cart";

const load = () => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

let lines = load();
const listeners = new Set();

const commit = (next) => {
    lines = next;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch (error) {
        // Storage full or blocked — the cart still works for this session.
    }
    listeners.forEach((listener) => listener(lines));
};

export const addToCart = (row, quantity, { view = "overall", lapsingEnabled = false, lookaheadDays = null } = {}) => {
    if (!row?.itemId || lines.some((line) => line.itemId === row.itemId)) return;
    const suggested = Number(quantity);
    commit([
        ...lines,
        {
            itemId: row.itemId,
            itemInventoryId: row.itemInventoryId || null,
            medicineName: row.medicineName || "Medicine",
            stripSize: row.stripSize ?? null,
            quantity: Number.isFinite(suggested) && suggested > 0 ? String(suggested) : "",
            // How the suggestion is worked out for this medicine's batch-wise info panel.
            view,
            lapsingEnabled,
            lookaheadDays, // the window the suggestion was worked out under (null = the API default)
        },
    ]);
};
export const removeFromCart = (itemId) => commit(lines.filter((line) => line.itemId !== itemId));
export const setCartQuantity = (itemId, quantity) => commit(lines.map((line) => (line.itemId === itemId ? { ...line, quantity } : line)));
export const clearCart = () => commit([]);

export const isValidCartQuantity = (quantity) => {
    const parsed = Number(quantity);
    return Number.isInteger(parsed) && parsed > 0;
};

export default function useProcurementCart() {
    const [snapshot, setSnapshot] = useState(lines);
    useEffect(() => {
        listeners.add(setSnapshot);
        setSnapshot(lines); // picks up anything changed between render and subscribe
        return () => listeners.delete(setSnapshot);
    }, []);
    return {
        lines: snapshot,
        has: (itemId) => snapshot.some((line) => line.itemId === itemId),
        add: addToCart,
        remove: removeFromCart,
        setQuantity: setCartQuantity,
        clear: clearCart,
    };
}
